/**
 * navigation/useNavCounts.ts
 * ──────────────────────────────────────────────────────────────────────────
 * The counts on the sidebar badges.
 *
 * Every one of these is a count of things WAITING, which is the only reason a
 * badge exists. Three of the five sources are gated, so each fetch tolerates
 * its own failure and contributes nothing rather than taking the sidebar down
 * with it: a workspace with no offboarding feed should show no settlement badge,
 * not an error.
 *
 * Refetched on `dataVersion`, so recording a spend that needs approval bumps
 * the Approvals badge without the user navigating anywhere.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback } from 'react';
import { useAsyncData } from '../lib/useAsyncData';
import { fetchAssignments } from '../services/cash/cash.service';
import { fetchApprovals } from '../services/approvals/approvals.service';
import { fetchReconciliations } from '../services/reconciliation/reconciliation.service';
import { fetchSyncLog } from '../services/sync/sync.service';
import { fetchSettlements } from '../services/settlement/settlement.service';
import { needsRouting } from '../schemas/reconciliation';
import type { ConsoleRole, GateStateResponse } from '../schemas/config';
import type { BadgeCounts } from './components/Sidebar';
import { useShell } from '../context/ShellContext';

/**
 * An empty result is the correct answer for a gated or forbidden source.
 * Generic over the whole payload rather than over an array, because three of
 * these endpoints return an envelope (`{ gates, rows }`) rather than a bare list.
 */
const tolerate = <T>(promise: Promise<T>, fallback: T): Promise<T> =>
  promise.catch(() => fallback);

/** Every gate closed — the safe fallback when a source could not be read. */
const CLOSED: GateStateResponse = {
  console: false,
  categories: false,
  variance_set: false,
  connectors: false,
  offboard_feed: false,
};

export function useNavCounts(role: ConsoleRole, categoriesConfigured: boolean): BadgeCounts {
  const { dataVersion } = useShell();

  const load = useCallback(async (): Promise<BadgeCounts> => {
    // The custodian console has no badges at all — its two views are always
    // both relevant, so there is nothing to count and nothing to fetch.
    if (role === 'custodian') return {};

    if (role === 'approver') {
      const pending = await tolerate(fetchApprovals('pending'), []);
      return { pending: pending.length };
    }

    const [assignments, pending, recons, sync, separations] = await Promise.all([
      tolerate(fetchAssignments({ status: 'active' }), []),
      tolerate(fetchApprovals('pending'), []),
      tolerate(fetchReconciliations(), { gates: CLOSED, reconciliations: [] }),
      tolerate(fetchSyncLog(), { gates: CLOSED, dead_letters: 0, rows: [] }),
      tolerate(fetchSettlements('open'), { gates: CLOSED, rows: [], exposure: [] }),
    ]);

    return {
      active: assignments.length,
      pending: pending.length,
      // Only counts still needing a decision — a matched count is not waiting.
      openRecon: recons.reconciliations.filter(needsRouting).length,
      undelivered: sync.rows.filter((row) => row.status !== 'DELIVERED').length,
      separations: separations.rows.filter((row) => row.state === 'open').length,
      // The Settings badge is the setup nudge: one unmet just-in-time setting.
      setup: categoriesConfigured ? 0 : 1,
    };
  }, [role, categoriesConfigured]);

  const { data } = useAsyncData(load, [role, categoriesConfigured], {
    version: dataVersion,
    // The badges are an affordance, not the page. A failure here must not put a
    // red toast on screen every time the shell renders.
    errorTitle: 'Could not refresh the navigation counts',
  });

  return data ?? {};
}
